import { Injectable } from '@angular/core';
import { LabResultsDataStorage } from './lab-results-data-storage';
import { TooltipFormatter } from '../../shared/tooltip-formatter';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { LabTestChartEvent } from '../../subjects/lab-tests-chart.component';
import { TooltipItem } from '../../shared/tooltip-item';
import { isEmpty } from 'lodash';
import { formatters } from '../../core';
import { tooltipClassName } from '../../core/tooltip-remover';
import { BoxPlotTooltip } from '@covance/ng-cvd3';
import { getFlaggedClassName } from '../../charts/chart-helper';

@Injectable()
export class LabResultSponsorTooltipRenderer {
    constructor(
        private storage: LabResultsDataStorage,
        private tooltipFormatter: TooltipFormatter
    ) { }

    public renderer = (key: string, x: string, y: string, value: LabTestChartEvent) => {
        const result = this.storage.testsDataSnapshotDictionary[value.data.rows[0]];
        const resultData = result.currentResult;
        const visitTitle = result.visitName || '';
        const unit = resultData ? resultData.unit || '' : '';
        const rangeString = resultData.rangeWithUnits;

        const subjectTitle = SubjectTitleRenderer.renderTitle({ data: result });

        const tooltipItems: TooltipItem[] = [];
        tooltipItems.push(new TooltipItem('Site Number', result.siteNumber || ''));
        tooltipItems.push(new TooltipItem('Subject', subjectTitle));
        tooltipItems.push(new TooltipItem('Visit', visitTitle));
        const valueTooltipItem = new TooltipItem('Value', `${y} ${unit}`);
        valueTooltipItem.contentClass = `${tooltipClassName}-${value.data.colorClassName}`;
        tooltipItems.push(valueTooltipItem);
        tooltipItems.push(new TooltipItem('Range', rangeString));
        tooltipItems.push(new TooltipItem('Collection Time', formatters.formatDateTime(result.visitCollectionDate)));
        tooltipItems.push(new TooltipItem('Accession', result.accessionNumber));

        if (!isEmpty(result.flags)) {
            tooltipItems.push(new TooltipItem('Flags', result.flags.map(f => f.fullName).join(' ')));
        }

        this.tooltipFormatter.clear();
        this.tooltipFormatter.addTooltipItems(tooltipItems);
        return this.tooltipFormatter.getTooltipContent();
    }

    public boxtooltip = (data: BoxPlotTooltip) => {

        const resultId = data.isBox ? (data.data.y as any)[0].rowId : data.data.rows[0];
        const labTestResult = this.storage.testsDataSnapshotDictionary[resultId];
        const units = labTestResult.unit || '';
        const tooltipItems: TooltipItem[] = [];
        const totalCount = (data.data.y as any).length;

        if (data.isBox) {
            tooltipItems.push(new TooltipItem('Total count', `${totalCount} points`));
            tooltipItems.push(new TooltipItem('Gender', data.key));
            tooltipItems.push(new TooltipItem('Min', `${data.whiskers[0]} ${units}`));
            tooltipItems.push(new TooltipItem('Max', `${data.whiskers[1]} ${units}`));
            tooltipItems.push(new TooltipItem('Median', `${data.quartiles[1]} ${units}`));
            tooltipItems.push(new TooltipItem('First Quartile', `${data.quartiles[0]} ${units}`));
            tooltipItems.push(new TooltipItem('Third Quartile', `${data.quartiles[2]} ${units}`));
        } else {
            const subjectTitle = SubjectTitleRenderer.renderTitle({ data: labTestResult });
            tooltipItems.push(new TooltipItem('Subject', subjectTitle));
            tooltipItems.push(new TooltipItem('Visit', labTestResult.visitName));
            if (labTestResult.visitCollectionDate) {
                tooltipItems.push(new TooltipItem('Collection Time', formatters.formatDateTime(labTestResult.visitCollectionDate)));
            }
            tooltipItems.push(new TooltipItem('Flags', labTestResult.flags.map(f => f.fullName).join(', ')));
            const valueTooltipItem = new TooltipItem('Value', `${labTestResult.currentResult.numericResult} ${units}`);
            valueTooltipItem.contentClass = `${tooltipClassName}-${getFlaggedClassName(labTestResult)}`;
            tooltipItems.push(valueTooltipItem);
            tooltipItems.push(new TooltipItem('Range', labTestResult.currentResult.rangeWithUnits));
            tooltipItems.push(new TooltipItem('Accession', labTestResult.accessionNumber));
        }

        this.tooltipFormatter.clear();
        this.tooltipFormatter.addTooltipItems(tooltipItems);
        return this.tooltipFormatter.getTooltipContent();
    }
}